import React, { useEffect, useState } from "react";
import axios from "axios";
import { NewSideBarAdmin } from "../../NewSideBarAdmin";
import "./AdminReclamations.css";

interface Reclamation {
  id: number;
  CodeReclamation: string;
  VisiteurId: number | null;
  Nom: string;
  Email: string | null;
  Telephone: string | null;
  TypeProbleme: string;
  RefCommande: string | null;
  Description: string;
  ImagePath: string | null;
  Statut: string;
  Date: string;
  DateReponse: string | null;
  ReponseAdmin: string | null;
}

const PROBLEM_LABELS: Record<string, string> = {
  commande_non_recue: "Commande non reçue",
  devis_refuse: "Devis non accepté / refusé",
  paiement: "Problème de paiement",
  article_endommage: "Article endommagé / incorrect",
  autre: "Autre demande"
};

export default function AdminReclamations() {
  const [reclamations, setReclamations] = useState<Reclamation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Modal / Reply states
  const [selectedRec, setSelectedRec] = useState<Reclamation | null>(null);
  const [replyText, setReplyText] = useState("");
  const [newStatus, setNewStatus] = useState("resolu");
  const [submittingReply, setSubmittingReply] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const fetchReclamations = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_PHP_APP_URL}/admin/reclamations`);
      setReclamations(res.data);
    } catch (err: any) {
      setError("Erreur lors de la récupération des réclamations.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReclamations();
  }, []);

  const handleOpenReplyModal = (rec: Reclamation) => {
    setSelectedRec(rec);
    setReplyText(rec.ReponseAdmin || "");
    setNewStatus(rec.Statut === "en_attente" ? "resolu" : rec.Statut);
  };

  const handleCloseReplyModal = () => {
    setSelectedRec(null);
  };

  const handleSendReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRec) return;

    setSubmittingReply(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_PHP_APP_URL}/admin/reclamations/${selectedRec.id}/reply`,
        {
          ReponseAdmin: replyText,
          Statut: newStatus
        }
      );
      setToastMessage("Réponse enregistrée avec succès !");
      setTimeout(() => setToastMessage(""), 3000);
      handleCloseReplyModal();
      fetchReclamations();
    } catch (err: any) {
      alert("Une erreur s'est produite lors de l'enregistrement de la réponse.");
    } finally {
      setSubmittingReply(false);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case "en_attente":
        return "badge-pending";
      case "en_cours":
        return "badge-inprogress";
      case "resolu":
        return "badge-resolved";
      case "ferme":
        return "badge-closed";
      default:
        return "badge-default";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "en_attente":
        return "En attente";
      case "en_cours":
        return "En cours";
      case "resolu":
        return "Résolu";
      case "ferme":
        return "Fermé";
      default:
        return status;
    }
  };

  return (
    <div className="admin-reclamations-layout">
      <NewSideBarAdmin />

      <div className="admin-reclamations-container">
        {toastMessage && (
          <div className="admin-toast">
            <i className="bi bi-check-circle-fill"></i> {toastMessage}
          </div>
        )}

        <header className="admin-reclamations-header">
          <h1>
            Gestion des <span>Réclamations</span> <i className="bi bi-shield-exclamation"></i>
          </h1>
          <p>Consultez, suivez et répondez aux réclamations de vos visiteurs et clients.</p>
        </header>

        {loading ? (
          <div className="admin-loader-wrapper">
            <div className="spinner-border text-warning" role="status">
              <span className="visually-hidden">Chargement...</span>
            </div>
            <p>Chargement des demandes...</p>
          </div>
        ) : error ? (
          <div className="alert alert-danger" role="alert">
            <i className="bi bi-exclamation-triangle-fill"></i> {error}
          </div>
        ) : reclamations.length === 0 ? (
          <div className="admin-empty-state">
            <i className="bi bi-inbox-fill"></i>
            <h3>Aucune réclamation</h3>
            <p>Toutes les réclamations des clients ont été traitées ou aucune n'a été déposée.</p>
          </div>
        ) : (
          <div className="admin-table-card">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Client / Contact</th>
                  <th>Référence</th>
                  <th>Type de Problème</th>
                  <th>Date de création</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {reclamations.map((rec) => (
                  <tr key={rec.id}>
                    <td>
                      <strong className="rec-code">{rec.CodeReclamation}</strong>
                    </td>
                    <td>
                      <div className="client-info-cell">
                        <strong>{rec.Nom}</strong>
                        {rec.Email && <span className="client-sub">{rec.Email}</span>}
                        {rec.Telephone && <span className="client-sub">{rec.Telephone}</span>}
                      </div>
                    </td>
                    <td>
                      {rec.RefCommande ? (
                        <span className="ref-badge">{rec.RefCommande}</span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      <span className="problem-type-txt">
                        {PROBLEM_LABELS[rec.TypeProbleme] || rec.TypeProbleme}
                      </span>
                    </td>
                    <td>
                      {new Date(rec.Date).toLocaleDateString("fr-FR", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit"
                      })}
                    </td>
                    <td>
                      <span className={`status-badge ${getStatusBadgeClass(rec.Statut)}`}>
                        {getStatusLabel(rec.Statut)}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn-reply-admin"
                        onClick={() => handleOpenReplyModal(rec)}
                      >
                        <i className="bi bi-reply-fill"></i> Répondre
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Reply/Details Modal */}
        {selectedRec && (
          <div className="admin-modal-overlay" onClick={handleCloseReplyModal}>
            <div className="admin-modal-box" onClick={(e) => e.stopPropagation()}>
              <div className="admin-modal-header">
                <h2>
                  <i className="bi bi-shield-exclamation"></i> Réclamation{" "}
                  <span>{selectedRec.CodeReclamation}</span>
                </h2>
                <button className="admin-modal-close" onClick={handleCloseReplyModal}>
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>

              <div className="admin-modal-body">
                <div className="admin-modal-grid">
                  <div className="admin-modal-info">
                    <label>Client</label>
                    <p>{selectedRec.Nom}</p>
                  </div>
                  {selectedRec.Email && (
                    <div className="admin-modal-info">
                      <label>Email</label>
                      <p>{selectedRec.Email}</p>
                    </div>
                  )}
                  {selectedRec.Telephone && (
                    <div className="admin-modal-info">
                      <label>Téléphone</label>
                      <p>{selectedRec.Telephone}</p>
                    </div>
                  )}
                  <div className="admin-modal-info">
                    <label>Référence</label>
                    <p>{selectedRec.RefCommande || "Aucune"}</p>
                  </div>
                  <div className="admin-modal-info" style={{ gridColumn: "1 / -1" }}>
                    <label>Type de problème</label>
                    <p>{PROBLEM_LABELS[selectedRec.TypeProbleme] || selectedRec.TypeProbleme}</p>
                  </div>
                  <div className="admin-modal-info" style={{ gridColumn: "1 / -1" }}>
                    <label>Description du client</label>
                    <div className="client-desc-box">{selectedRec.Description}</div>
                  </div>

                  {selectedRec.ImagePath && (
                    <div className="admin-modal-info" style={{ gridColumn: "1 / -1" }}>
                      <label>Pièce jointe / Capture d'écran</label>
                      <div className="image-attach-preview">
                        <a
                          href={`${process.env.REACT_APP_PHP_APP_URL}/storage/${selectedRec.ImagePath}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <img
                            src={`${process.env.REACT_APP_PHP_APP_URL}/storage/${selectedRec.ImagePath}`}
                            alt="Preuve"
                          />
                        </a>
                      </div>
                    </div>
                  )}
                </div>

                <hr className="admin-modal-divider" />

                <form onSubmit={handleSendReply}>
                  <div className="form-group" style={{ marginBottom: 18 }}>
                    <label className="admin-label">Statut de la demande</label>
                    <select
                      className="form-select admin-select"
                      value={newStatus}
                      onChange={(e) => setNewStatus(e.target.value)}
                    >
                      <option value="en_attente">En attente</option>
                      <option value="en_cours">En cours de traitement</option>
                      <option value="resolu">Résolu</option>
                      <option value="ferme">Fermé / Archivé</option>
                    </select>
                  </div>

                  <div className="form-group" style={{ marginBottom: 20 }}>
                    <label className="admin-label">Réponse à envoyer au client</label>
                    <textarea
                      required
                      className="form-control admin-textarea"
                      placeholder="Tapez votre réponse explicative ou de résolution..."
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                    />
                  </div>

                  <div className="admin-modal-footer">
                    <button
                      type="button"
                      className="btn-cancel"
                      onClick={handleCloseReplyModal}
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      className="btn-submit"
                      disabled={submittingReply}
                    >
                      {submittingReply ? (
                        <>
                          <div className="spinner-border spinner-border-sm" role="status"></div>{" "}
                          Enregistrement...
                        </>
                      ) : (
                        <>Enregistrer la réponse</>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
